import { ImageItem } from "@kontent-ai/gatsby-components/dist/image-element";
import React from "react";
import { Cta } from "../../elements/Cta/Cta";
import { CustomRichTextElement } from "../../elements/CustomRichTextElement/CustomRichTextElement";
import { KontentImageElement } from "../../elements/KontentImageElement/KontentImageElement";
import * as styles from "./FiftyFiftyFeaturedContent.module.css";
import {
  ComponentWidth,
  ComponentProps,
  KontentRichText,
} from "../../../types";
import { SectionWrapper } from "../../containers/SectionWrapper";
import { darkBrandColors } from "../../../lib/color";

interface Props extends ComponentWidth, ComponentProps {
  orientation?: "text_left" | "text_right";
  style?: string;
  textAlignment?: "left" | "center";
  contentCategory?: string;
  subhead: string;
  dateTime?: string;
  text?: KontentRichText;
  ctas?: { label: string; href: string; target: string }[];
  image: {
    item: ImageItem;
    alt: string;
  };
  id: string;
}

export const FiftyFiftyFeaturedContent: React.FC<Props> = ({
  orientation,
  style,
  textAlignment,
  contentCategory,
  subhead,
  dateTime,
  text,
  ctas,
  width,
  image,
  ...props
}) => {
  //legacy styles are "green_background" and "cream_background" these map to black and offwhite in new color palette
  const backgroundColor =
    style?.split("background_")[1]?.replace(/_/g, "-") ||
    (style === "green_background" ? "black" : "off-white");
  const textColor = darkBrandColors.includes(backgroundColor)
    ? "white"
    : "black";

  const buildSectionClassName = () => {
    let className = styles.fiftyfiftyfeaturedcontent;

    if (orientation === "text_right") {
      className += " " + styles.reverse;
    }

    if (width === "contained") {
      className += " " + styles.contained;
    }

    return className;
  };

  const sectionClassName = buildSectionClassName();

  const renderCtas = () => {
    if (!ctas || ctas.length === 0) {
      return;
    }

    if (ctas && ctas?.length === 1) {
      return (
        <div
          className={styles.ctas + " " + styles[`count${ctas.length}`]}
          data-kontent-element-codename="link_configuration__link"
        >
          {ctas?.map((cta, idx) => {
            return (
              <Cta
                key={`cta-${idx}`}
                href={cta.href}
                target={cta.target}
                label={cta.label}
                invert={textColor === "white"}
              />
            );
          })}
        </div>
      );
    }

    return (
      <ul
        className={styles.ctas + " " + styles[`count${ctas?.length}`]}
        data-kontent-element-codename="link_configuration__link"
      >
        {ctas?.map((cta, idx) => {
          return (
            <li key={`cta-${idx}`}>
              <Cta
                href={cta.href}
                target={cta.target}
                label={cta.label}
                invert={textColor === "white"}
              />
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <SectionWrapper {...props}>
      <div className={sectionClassName}>
        <div
          className={styles.textPanel}
          style={{
            backgroundColor: `var(--${backgroundColor})`,
            color: `var(--${textColor})`,
          }}
        >
          <div
            className={
              textAlignment === "center"
                ? `${styles.textContainer} ${styles.center}`
                : styles.textContainer
            }
          >
            {contentCategory && (
              <div
                className={styles.contentCategory}
                data-kontent-element-codename="content_category_tax"
              >
                {contentCategory}
              </div>
            )}
            <h2>{subhead}</h2>
            {dateTime && (
              <div
                className={styles.date}
                data-kontent-element-codename="date_time"
              >
                {dateTime}
              </div>
            )}
            {text && (
              <CustomRichTextElement
                content={text}
                data-kontent-element-codename="text"
              />
            )}

            {renderCtas()}
          </div>
        </div>
        <div
          className={styles.imagePanel}
          data-kontent-element-codename="image"
        >
          <KontentImageElement
            aspectRatio={1.45}
            objectFit="cover"
            layout="fullWidth"
            image={image?.item}
            alt={image?.alt}
          />
        </div>
      </div>
    </SectionWrapper>
  );
};
